import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { cancelServiceOrder, applyCancellationFee } from '../../services/zohoServiceOrderService';
import { STANDARD_FEE_PRESETS, presetFee, presetLabel, useJobPrice, type StandardFeePreset } from '../../utils/cancellationFee';
import { useAuth } from '../../contexts';
import { OWNER_ROLES, MANAGER_ROLES, type Role } from '@rsmaids/shared/constants/roles';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonTitle,
  IonContent,
  IonIcon,
  IonFooter,
  IonSpinner,
  IonSkeletonText,
  IonCard,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonNote,
  IonText,
  IonAvatar,
  IonChip,
  useIonAlert,
  useIonRouter,
} from '@ionic/react';
import {
  ellipsisVertical,
  locationSharp,
  createOutline,
  documentOutline,
  calendarOutline,
  personOutline,
  mailOutline,
  close,
  closeOutline,
  documentTextOutline,
  add,
  callOutline,
  chatbubbleOutline,
  receiptOutline,
  checkmarkCircle,
  checkmarkDone,
  alertCircleOutline,
  timeOutline,
  cubeOutline,
  cardOutline,
  documentAttachOutline,
} from 'ionicons/icons';
import { BookingItem } from '@rsmaids/shared/types/bookings';
import styles from './BookingDetails.module.scss';
import cancellationStyles from './CancellationModal.module.scss';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { contactsCache } from '@rsmaids/shared/services/api/contactsCacheService';
import SheetModal from '../../components/SheetModal';
import ConversationThread from '../../components/ConversationThread';
import CopyableIdTitle from '../../components/CopyableIdTitle';
import Contact from './Contact';
import { openIvrDialer } from '../../desktop/components/IvrPanel/ivrPanelBus';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { InboxService } from '@rsmaids/shared/services/api/inboxService';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { getBookingByIdFromCache } from '@rsmaids/shared/services/api/bookingsCacheService';
import { JobMediaManager } from '../../components/JobMediaManager';
import PortalDropdown from '../../components/PortalDropdown';
import HelcimCheckoutModal from '../../components/HelcimCheckoutModal';
import CreateInvoice from '../CreateInvoice';
import ServiceOrderDetail from '../ServiceOrderDetail';
import Invoice from '../Invoice';
import { getBookingPaymentStatus, getZohoInvoiceIdForBooking } from '../../services/zohoInvoicingService';
import {
  getServiceOrder,
  getServiceOrderById,
  completeBookingInvoice,
  type ZohoSalesOrder,
} from '../../services/zohoServiceOrderService';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../../i18n/locale';

// Wix CMS Collection ID for job media
const JOB_MEDIA_COLLECTION_ID = 'JobMedia';

interface BookingDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId?: string;
  hidePayment?: boolean;
}

const BookingDetailsModal: React.FC<BookingDetailsModalProps> = ({
  isOpen,
  onClose,
  bookingId,
  hidePayment: hidePaymentProp = false,
}) => {
  const { t } = useTranslation();
  // Prices and totals (the price, balance due, invoices, service order and the options menu) are for owners and managers only,
  // whichever screen opened this (a missing role counts as not allowed). The prop still hides them for a screen that asks for it.
  const { authState } = useAuth();
  const role = authState.user?.role as Role | undefined;
  const hidePayment = hidePaymentProp || !role || ![...OWNER_ROLES, ...MANAGER_ROLES].includes(role);
  const ionRouter = useIonRouter();
  // "View Contact" (and similar) must not navigate while this modal is still
  // dismissing — on a slow device that races IonRouterOutlet's transition and
  // lands on a blank/black outlet. Stash the intent here and run it from
  // onDidDismiss, once the modal is fully gone.
  const pendingDismissActionRef = React.useRef<null | (() => void)>(null);
  const unsubscribeRef = React.useRef<null | (() => void)>(null);
  const runPendingDismissAction = () => {
    onClose();
    const action = pendingDismissActionRef.current;
    pendingDismissActionRef.current = null;
    if (action) action();
  };
  const [booking, setBooking] = useState<BookingItem | null>(null);
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);
  // The booking's staff notes from Wix (loaded further down); declared here because the
  // "Manage booking" menu below reads it to say "Add Note" or "Edit Note"
  const [bookingNotes, setBookingNotes] = useState('');
  const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  // Wix attendance = ATTENDED. Kept apart from isCompleted because loadBooking() (re-run on every
  // booking-refresh-trigger) resets isCompleted from the booking record, which never carries it.
  const [isAttended, setIsAttended] = useState<boolean>(false);
  // Complete already updates this screen's own state, so the refresh signal it sends (for the
  // calendar / lists) must not make this screen reload itself — that flashed the whole page.
  const ignoreOwnRefreshUntilRef = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [cancellationMessage, setCancellationMessage] = useState('');
  const [notifyClient, setNotifyClient] = useState(true);
  const [waiveFee, setWaiveFee] = useState(true);
  const [feeStep, setFeeStep] = useState<'selection' | 'type'>('selection'); // Track which step we're on
  const [feeType, setFeeType] = useState<'standard' | 'custom' | null>(null);
  const [customFeeAmount, setCustomFeeAmount] = useState('');
  const [standardPreset, setStandardPreset] = useState<StandardFeePreset | null>(null);
  const jobPrice = useJobPrice(booking?.id, feeType === 'standard');
  const [isHelcimCheckoutOpen, setIsHelcimCheckoutOpen] = useState(false);
  const [isCreateInvoiceOpen, setIsCreateInvoiceOpen] = useState(false);
  const [isServiceOrderOpen, setIsServiceOrderOpen] = useState(false);
  const [invoiceModalId, setInvoiceModalId] = useState<string | null>(null);
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
  const closeInvoiceModal = useCallback(() => {
    setIsInvoiceOpen(false);
    setInvoiceModalId(null);
  }, []);
  const [showCollectPaymentMenu, setShowCollectPaymentMenu] = useState(false);
  const collectPaymentTriggerRef = useRef<HTMLIonButtonElement>(null);
  const collectPaymentPortalRef = useRef<HTMLDivElement>(null);

  // Zoho Sales Order linked to this booking
  const [salesOrder, setSalesOrder] = useState<ZohoSalesOrder | null>(null);
  // True until the linked service order has been looked up: an invoiced order also means the
  // job is finished, so the Complete button stays hidden until we know (no flash-then-hide).
  const [isServiceOrderLoading, setIsServiceOrderLoading] = useState(true);
  // Wix attendance (ATTENDED = job done). Also gates the Complete button so it doesn't flash
  // for jobs that are already attended.
  const [isAttendanceLoading, setIsAttendanceLoading] = useState(true);
  const [paymentStatusOverride, setPaymentStatusOverride] = useState<string | null>(null);

  const [presentAlert] = useIonAlert();
  // Get the data service
  const dataService = DataServiceFactory.getService();

  // Not-ready early-out is computed during render (React's "adjust state
  // when a dependency changes" pattern) rather than as setState calls
  // inside the effect below, which now only handles the actual load.
  const bookingDetailsReady = Boolean(isOpen && bookingId);
  const [prevBookingDetailsReady, setPrevBookingDetailsReady] = useState(bookingDetailsReady);
  if (prevBookingDetailsReady !== bookingDetailsReady) {
    setPrevBookingDetailsReady(bookingDetailsReady);
    if (!bookingDetailsReady) {
      setBooking(null);
      setError(null);
      setSalesOrder(null);
      setPaymentStatusOverride(null);
      setIsCompleted(false);
      setIsAttended(false);
      setIsInitialLoading(false);
    }
  }

  // Initial data loading
  useEffect(() => {
    if (!bookingDetailsReady || !bookingId) return;

    let isActive = true;

    Promise.resolve().then(() => {
      if (!isActive) return;

      setBooking(null);
      setError(null);
      setSalesOrder(null);
      setIsServiceOrderLoading(true);
      setIsAttendanceLoading(true);
      setPaymentStatusOverride(null);
      setIsCompleted(false);
      setIsAttended(false);
      setIsInitialLoading(true);

      const loadBooking = async () => {
        setError(null);

        const storedBooking = DataStorageService.getData<BookingItem>('selectedBooking');
        const canSeedFromStorage = Boolean(
          storedBooking && (storedBooking.id === bookingId || storedBooking.bookingId === bookingId)
        );

        if (canSeedFromStorage && storedBooking) {
          setBooking(storedBooking);
          setIsCompleted(storedBooking.isCompleted || false);
          setIsInitialLoading(false);
        } else {
          setIsInitialLoading(true);
        }

        try {
          let bookingData = await getBookingByIdFromCache(bookingId);

          // Fall back to Wix-backed API when not yet synced in Firestore
          if (!bookingData) {
            bookingData = await dataService.bookings.getBookingById(bookingId);
          }

          if (!isActive) return;

          if (bookingData) {
            setBooking(bookingData);
            setIsCompleted(bookingData.isCompleted || false);
          } else {
            // Keep previously seeded storage booking if it matches this id
            if (canSeedFromStorage && storedBooking) {
              setBooking(storedBooking);
              setIsCompleted(storedBooking.isCompleted || false);
            } else {
              setError(t('pages.bookingSystem.BookingDetails.bookingNotFound'));
            }
          }
        } catch (error) {
          console.error('Error loading booking:', error);

          if (!isActive) return;

          // Keep previously seeded storage booking if it matches this id
          if (canSeedFromStorage && storedBooking) {
            setBooking(storedBooking);
            setIsCompleted(storedBooking.isCompleted || false);
          } else {
            setError(t('pages.bookingSystem.BookingDetails.failedToLoadBookingDetails'));
          }
        } finally {
          if (isActive) setIsInitialLoading(false);
        }
      };

      loadBooking();

      // A job counts as completed when Wix has it as ATTENDED. Bookings invoiced before
      // attendance was recorded are treated as attended too (see the sales-order load below).
      dataService.bookings.getBookingsAttendance([bookingId])
        .then(statuses => {
          if (!isActive) return;
          if (statuses[bookingId] === 'ATTENDED') setIsAttended(true);
        })
        .catch(err => console.error('Failed to load booking attendance:', err))
        .finally(() => { if (isActive) setIsAttendanceLoading(false); });

      // The service order and the Zoho payment status are money: only owners and managers request them. A staff or lead account
      // never receives them (the server refuses them too); Complete is decided on the server (see handleStatusChange).
      if (hidePayment) {
        setIsServiceOrderLoading(false);
      } else {
        // Load the linked Zoho Sales Order (used by View Service Order menu item)
        getServiceOrder(bookingId)
          .then(so => {
            if (!isActive) return;
            setSalesOrder(so);
          })
          .catch(err => console.error('Failed to load service order:', err))
          .finally(() => { if (isActive) setIsServiceOrderLoading(false); });

        // Resolve payment status directly from Zoho for this booking when available.
        getBookingPaymentStatus(bookingId)
          .then(status => {
            if (!isActive) return;
            const normalized = (status.paymentStatus || '').toLowerCase();
            setPaymentStatusOverride(normalized || null);
            setBooking(prev => prev ? {
              ...prev,
              zohoPaid: status.paid,
              isPaid: status.paid,
              zohoPaymentStatus: normalized || prev.zohoPaymentStatus,
            } : prev);
          })
          .catch(err => {
            console.error('Failed to load booking payment status:', err);
          });
      }

      // Listen for booking updates (both same-tab and cross-tab)
      unsubscribeRef.current = DataStorageService.setupStorageListener((key: string) => {
        if (key === 'booking-refresh-trigger') {
          if (Date.now() < ignoreOwnRefreshUntilRef.current) return;
          loadBooking();
        }
      });
    });

    return () => {
      isActive = false;
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
  }, [bookingDetailsReady, bookingId, dataService.bookings, hidePayment, t]);

  // Complete: records the job as ATTENDED in Wix (the completion status — counts toward the
  // completion rate and notifies the business), and, for customers whose billing config is
  // "per sales order" (the default), also creates the instant invoice from the linked service
  // order. Grouped / manual customers are only marked attended; they are invoiced separately.
  // The billing decision (billing config, linked order, already invoiced) and the invoice are the server's job, so no role
  // has to receive the billing record or the service order just to press Complete.
  const handleStatusChange = async () => {
    if (!booking) return;

    setIsUpdating(true);

    try {
      // A failure here (including not being able to read the billing config) stops before anything is recorded, rather than
      // risk an unwanted invoice
      const { completion, invoiceNumber } = await completeBookingInvoice(bookingId || booking.id, booking.contact?.id);

      if (completion === 'noServiceOrder') {
        presentAlert({
          header: t('pages.bookingSystem.BookingDetails.noServiceOrder'),
          message: t('pages.bookingSystem.BookingDetails.thisBookingHasNoLinkedService'),
          cssClass: 'glass-alert',
          buttons: ['OK'],
        });
        return;
      }

      const createdInvoice = completion === 'created';
      if (createdInvoice) {
        setSalesOrder(prev => prev ? { ...prev, status: 'invoiced' } : prev);
        console.log('[BookingDetails] Instant invoice created:', invoiceNumber);
      }
      try {
        await dataService.bookings.setBookingAttended(booking.id);
      } catch (attendanceError) {
        // The invoice already exists and an invoiced booking counts as attended, so don't
        // report a failure for it; for non-invoicing customers attendance is the whole action.
        if (!createdInvoice) throw attendanceError;
        console.error('[BookingDetails] Invoice created but attendance was not recorded:', attendanceError);
      }
      setIsAttended(true);
      ignoreOwnRefreshUntilRef.current = Date.now() + 3000;
      // Attended is a single-booking change the live listener already delivers: tell the agenda not to reload for it.
      DataStorageService.storeData('booking-refresh-skip-agenda', Date.now());
      DataStorageService.storeData('booking-refresh-trigger', Date.now());
    } catch (error) {
      presentAlert({
        header: t('pages.bookingSystem.BookingDetails.invoiceFailed'),
        message: t('pages.bookingSystem.BookingDetails.theBookingWasNotMarkedComplete'),
        cssClass: 'glass-alert',
        buttons: ['OK'],
      });
      console.error('Failed to update status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelbooking = async () => {
    console.log('handleCancelbooking called with booking:', booking?.id, 'waiveFee:', waiveFee);
    if (booking?.id) {
      try {
        setIsUpdating(true);
        // Waive: void the Zoho SO. Apply custom fee: zero the line items and
        // add a Cancellation Fee line instead of voiding. Standard Fee has
        // no defined amount yet (see Agenda.tsx's matching comment) — falls
        // back to voiding, same as Waive, until a real amount source exists.
        // Order: unattended (done inside cancelBooking) -> cancel in Wix -> take the job off the invoice and void
        // its order in Zoho. A fee keeps the old order (Zoho first): its flag stops the cancel webhook voiding the order.
        const isFeeCancel = !waiveFee && ((feeType === 'custom') || (feeType === 'standard' && !!standardPreset));
        if (!isFeeCancel) {
          await dataService.bookings.cancelBooking(booking.id, waiveFee);
        }
        try {
          if (!waiveFee && feeType === 'custom') {
            await applyCancellationFee(booking.id, parseFloat(customFeeAmount));
          } else if (!waiveFee && feeType === 'standard' && standardPreset) {
            await applyCancellationFee(booking.id, presetFee(standardPreset));
          } else {
            await cancelServiceOrder(booking.id);
          }
        } catch (zohoErr) {
          console.error('[BookingDetails] Zoho SO update failed:', zohoErr);
          // Wix is already cancelled on the void path, so there is nothing to go back to: the cancel webhook retries Zoho.
          const continueAnyway = !isFeeCancel || await new Promise<boolean>(resolve => {
            presentAlert({
              header: t('pages.bookingSystem.BookingDetails.serviceOrderError'),
              message: !waiveFee
                ? t('pages.bookingSystem.BookingDetails.theCancellationFeeCouldNotBe')
                : t('pages.bookingSystem.BookingDetails.theServiceOrderCouldNotBe'),
              cssClass: 'logout-alert',
              buttons: [
                { text: t('pages.bookingSystem.BookingDetails.cancel'), role: 'cancel', handler: () => resolve(false) },
                { text: t('pages.bookingSystem.BookingDetails.continueAnyway'), handler: () => resolve(true) },
              ],
            });
          });
          if (!continueAnyway) { setIsUpdating(false); return; }
        }
        if (isFeeCancel) {
          await dataService.bookings.cancelBooking(booking.id, waiveFee);
        }

        // TODO: Send email notification to client if notifyClient is true
        // with the cancellation message

        // Clear cached booking data
        DataStorageService.removeData(`booking-${booking.id}`);
        DataStorageService.removeData('selectedBooking');

        // Trigger refresh in calendar/dashboard
        DataStorageService.storeData('booking-refresh-trigger', Date.now());

        // Close dialogs and reset form
        setIsCancellationModalOpen(false);
        setFeeStep('selection');
        setWaiveFee(true);
        setNotifyClient(true);
        setCancellationMessage('');
        setFeeType(null);
        setCustomFeeAmount('');
        onClose();
      } catch (error) {
        console.error('Error cancelling booking:', error);
        setError(t('pages.bookingSystem.BookingDetails.failedToCancelBooking'));
      } finally {
        setIsUpdating(false);
      }
    } else {
      console.warn('handleCancelbooking called but no booking.id available');
    }
  };

  const handleCancelClick = () => {
    // Show the cancellation options sheet modal
    setIsCancellationModalOpen(true);
  };

  const handleBookAgain = useCallback(() => {
    if (booking && booking.contact) {
      const contactData = {
        id: booking.contact.id,
        name: booking.contact.name,
        email: booking.contact.email,
        phone: booking.contact.phone
      };

      // Store contact data for the booking page to prefill
      DataStorageService.storeData('selectedContact', contactData);

      // Trigger booking modal opening without closing the details modal
      setTimeout(() => {
        DataStorageService.storeData('open-booking-modal', 'new');
      }, 300);
    }
  }, [booking]);

  const menuOptions = useMemo(() => booking ? [
    {
      label: t('pages.bookingSystem.BookingDetails.editBooking'),
      icon: createOutline,
      onClick: () => {
        // Store the complete booking data for editing
        DataStorageService.storeData(`edit-booking-${booking.id}`, booking);
        // Also store the contact data separately in case the form needs it
        if (booking.contact) {
          DataStorageService.storeData('selectedContact', booking.contact);
        }
        // Trigger booking modal opening without closing the details modal
        setTimeout(() => {
          DataStorageService.storeData('edit-booking-modal', booking.id);
        }, 100);
        setIsOptionsModalOpen(false);
      }
    },
    {
      label: salesOrder ? t('pages.bookingSystem.BookingDetails.viewServiceOrder', { salesorder_number: salesOrder.salesorder_number }) : t('pages.bookingSystem.BookingDetails.viewServiceOrder2'),
      icon: receiptOutline,
      onClick: () => {
        setIsOptionsModalOpen(false);
        if (salesOrder) {
          setIsServiceOrderOpen(true);
        }
      }
    },
    {
      label: t('pages.bookingSystem.BookingDetails.bookAgain'),
      icon: calendarOutline,
      onClick: () => {
        handleBookAgain();
        setIsOptionsModalOpen(false);
      }
    },
    {
      label: t('pages.bookingSystem.BookingDetails.viewContact'),
      icon: personOutline,
      onClick: () => {
        if (!booking?.contact?.id) return;
        const contactId = booking.contact.id;
        DataStorageService.storeData(`contact-${contactId}`, booking.contact);
        setIsOptionsModalOpen(false);
        // Open the contact in a modal over these details — they stay open underneath, so going
        // back from the contact returns here. (It used to close these details first and then
        // navigate to the contact's page.)
        setContactModalId(contactId);
      }
    },
    {
      label: t('pages.bookingSystem.BookingDetails.viewInvoice'),
      icon: documentTextOutline,
      onClick: async () => {
        const bId = booking.id;
        setIsOptionsModalOpen(false);
        // The invoice page expects a Zoho invoice_id, not a booking ID. The
        // loaded service order already lists its linked invoices, so open
        // straight away with that ID — exactly like View Service Order.
        const linkedInvoiceId = salesOrder?.invoices?.[0]?.invoice_id;
        if (linkedInvoiceId) {
          setInvoiceModalId(linkedInvoiceId);
          setIsInvoiceOpen(true);
          return;
        }
        // Not in the loaded order (e.g. invoiced moments ago) — open with the
        // loading screen and re-fetch the order, or fall back to a booking lookup.
        setInvoiceModalId(null);
        setIsInvoiceOpen(true);
        let zohoInvoiceId: string | null = null;
        try {
          if (salesOrder) {
            const detail = await getServiceOrderById(salesOrder.salesorder_id);
            zohoInvoiceId = detail?.invoices?.[0]?.invoice_id ?? null;
          } else {
            zohoInvoiceId = await getZohoInvoiceIdForBooking(bId);
          }
        } catch (err) {
          console.error('[BookingDetails] invoice lookup failed', err);
        }
        if (!zohoInvoiceId) {
          setIsInvoiceOpen(false);
          presentAlert({
            header: t('pages.bookingSystem.BookingDetails.noInvoiceFound'),
            message: t('pages.bookingSystem.BookingDetails.thisBookingDoesNotHaveAn'),
            cssClass: 'glass-alert',
            buttons: ['OK'],
          });
          return;
        }
        setInvoiceModalId(zohoInvoiceId);
      }
    },
    {
      label: bookingNotes.trim() ? t('pages.bookingSystem.BookingDetails.editNote') : t('pages.bookingSystem.BookingDetails.addNote'),
      icon: add,
      onClick: () => {
        // Opens the booking form (the same as "Edit booking") straight onto its Notes section.
        // This used to store a 'navigate-to-notes' key that nothing listened to, so nothing
        // happened. The form reads the flag below when it opens.
        DataStorageService.storeData(`edit-booking-${booking.id}`, booking);
        if (booking.contact) {
          DataStorageService.storeData('selectedContact', booking.contact);
        }
        DataStorageService.storeData('edit-booking-focus-notes', booking.id);
        setTimeout(() => {
          DataStorageService.storeData('edit-booking-modal', booking.id);
        }, 100);
        setIsOptionsModalOpen(false);
      }
    },
    {
      label: t('pages.bookingSystem.BookingDetails.cancelBooking2'),
      icon: closeOutline,
      onClick: () => {
        setIsOptionsModalOpen(false);
        handleCancelClick();
      },
      color: 'danger'
    }
  ] : [], [booking, handleBookAgain, salesOrder, presentAlert, bookingNotes, t]);

  const completionChip = (): { color: string; label: string; icon: string } => {
    if (isCompleted || isAttended) return { color: 'success', label: t('pages.bookingSystem.BookingDetails.completed'), icon: checkmarkDone };
    if (booking?.status === 'cancelled') return { color: 'danger', label: t('pages.bookingSystem.BookingDetails.cancelled'), icon: closeOutline };
    if (booking?.status === 'pending') return { color: 'warning', label: t('pages.bookingSystem.BookingDetails.pending'), icon: alertCircleOutline };
    return { color: 'primary', label: booking?.status || t('pages.bookingSystem.BookingDetails.active'), icon: alertCircleOutline };
  };

  const getPaymentStatusBadge = (b: BookingItem | null): { label: string; color: string } | null => {
    if (!b) return null;
    const payStatus = (paymentStatusOverride ?? b.zohoPaymentStatus)?.toLowerCase();
    if (payStatus === 'paid') return { label: t('pages.bookingSystem.BookingDetails.paid'), color: 'success' };
    if (payStatus === 'overdue') return { label: t('pages.bookingSystem.BookingDetails.overdue'), color: 'danger' };
    if (payStatus === 'unpaid') return { label: t('pages.bookingSystem.BookingDetails.unpaid'), color: 'warning' };
    if (payStatus === 'partially_paid') return { label: t('pages.bookingSystem.BookingDetails.partial'), color: 'warning' };
    if (payStatus === 'sent' || payStatus === 'viewed') return { label: t('pages.bookingSystem.BookingDetails.unpaid'), color: 'warning' };
    if (payStatus === 'draft') return { label: t('pages.bookingSystem.BookingDetails.draft'), color: 'medium' };
    if (b.zohoPaid === true || b.isPaid === true) return { label: t('pages.bookingSystem.BookingDetails.paid'), color: 'success' };

    // If we have an SO but no concrete invoice status yet, show a meaningful payment state.
    if (salesOrder?.status === 'invoiced') return { label: t('pages.bookingSystem.BookingDetails.unpaid'), color: 'warning' };
    if (salesOrder) return { label: t('pages.bookingSystem.BookingDetails.pending'), color: 'primary' };

    if (b.serviceType === 'Free Estimate') return { label: t('pages.bookingSystem.BookingDetails.free'), color: 'tertiary' };
    return { label: t('pages.bookingSystem.BookingDetails.noInvoice'), color: 'medium' };
  };

  // Paid per any source (Zoho status, override, or booking flag) — the footer
  // then drops "Amount Due" and the redundant chip; the disabled button says Paid.
  const isPaidInFull = !!booking && (getPaymentStatusBadge(booking)?.label === 'Paid' || booking.isPaid === true);

  const getOrderBadge =(): { label: string; color: string } | null => {
    if (isBookingFinished) return null;

    const soStatus = salesOrder?.status;
    if (soStatus === 'draft') return { label: t('pages.bookingSystem.BookingDetails.draft'), color: 'medium' };
    if (soStatus === 'open') return { label: t('pages.bookingSystem.BookingDetails.open'), color: 'primary' };
    // While the booking is still open (not yet finished), "Invoiced" belongs
    // here next to the booking status — the dedicated .invoicedBadge (icon +
    // "Invoiced", next to the Complete button) only renders once
    // isBookingFinished, so the two never show at the same time.
    if (soStatus === 'invoiced') return { label: t('pages.bookingSystem.BookingDetails.invoiced'), color: 'primary' };
    if (soStatus === 'void' || soStatus === 'cancelled') return { label: t('pages.bookingSystem.BookingDetails.cancelled'), color: 'medium' };
    if (salesOrder) return { label: t('pages.bookingSystem.BookingDetails.created'), color: 'primary' };
    return { label: t('pages.bookingSystem.BookingDetails.noOrder'), color: 'medium' };
  };

  // Keep complete/invoice disabled only when we're confident the appointment
  // starts in the future. Prefer booking.startTime directly (it may already
  // contain full date+time), and fall back to booking.date + time-only strings.
  const isBeforeAppointment = useMemo(() => {
    if (!booking) return false;

    const now = new Date().getTime();

    // 1) Prefer a direct Date instance from the booking payload.
    if (booking.startTime instanceof Date && !isNaN(booking.startTime.getTime())) {
      return booking.startTime.getTime() > now;
    }

    // 2) If startTime is an ISO-ish datetime string, parse it directly.
    if (typeof booking.startTime === 'string' && booking.startTime.trim()) {
      const directParsed = new Date(booking.startTime);
      if (!isNaN(directParsed.getTime()) && /\d{4}-\d{2}-\d{2}T/.test(booking.startTime)) {
        return directParsed.getTime() > now;
      }
    }

    // 3) Fallback for time-only strings (e.g. "3:30 PM") using booking.date.
    if (!booking.date) return false;
    const dateParts = booking.date.split('-');
    if (dateParts.length !== 3) return false;

    const startTimeValue =
      typeof booking.startTime === 'string' && booking.startTime.trim()
        ? booking.startTime
        : booking.startTime instanceof Date
          ? booking.startTime.toTimeString().substring(0, 5)
          : '';
    if (!startTimeValue) return false;

    const parsedTime = new Date(`2000/01/01 ${startTimeValue}`);
    if (isNaN(parsedTime.getTime())) return false;

    const appointmentStart = new Date(
      parseInt(dateParts[0], 10),
      parseInt(dateParts[1], 10) - 1,
      parseInt(dateParts[2], 10),
      parsedTime.getHours(),
      parsedTime.getMinutes(),
    );

    return appointmentStart.getTime() > now;
  }, [booking]);

  const isOrderInvoiced = salesOrder?.status === 'invoiced';
  // Invoiced is billing, not attendance: a prepaid visit is invoiced and paid before the job is done.
  const isBookingFinished = isCompleted || isAttended;
  const displayPrice = useMemo(() => {
    if (typeof salesOrder?.total === 'number' && Number.isFinite(salesOrder.total)) {
      return salesOrder.total;
    }

    return 0;
  }, [salesOrder]);

  const displayPriceText = `$${displayPrice.toFixed(2)}`;

  // Message the client: opens their own conversation in a modal on top of these details, so
  // going back returns here (nothing closes). It opens at once as a blank shell — exactly what
  // the Inbox does when you start a conversation from a contact: a temporary id plus
  // `isBlankShell`, so there is no loading screen — while the contact's real conversation is
  // found or created; the thread is then handed the real id through the same
  // `realConversationFound` event the Inbox uses.
  const [clientThread, setClientThread] = useState<{ id: string; contactName?: string; avatarUrl?: string; isShell?: boolean } | null>(null);
  // The booking's staff notes as Wix has them (its calendar event notes). The booking record the
  // details are built from doesn't carry them, so they are read separately — when these details
  // open, and again after the booking form saves (it signals that with 'booking-refresh-trigger').
  const notesBookingId = booking?.id;
  useEffect(() => {
    if (!isOpen || !notesBookingId) return;
    let cancelled = false;
    const notesApi = DataServiceFactory.getService().bookings as unknown as { getBookingNotes: (id: string) => Promise<string> };
    const load = () => {
      notesApi.getBookingNotes(notesBookingId).then(text => {
        if (!cancelled) setBookingNotes(text);
      });
    };
    load();
    const removeListener = DataStorageService.setupStorageListener((key: string) => {
      // Wix needs a moment to show the saved change
      if (key === 'booking-refresh-trigger' && Date.now() >= ignoreOwnRefreshUntilRef.current) setTimeout(load, 1500);
    });
    return () => {
      cancelled = true;
      removeListener();
    };
  }, [isOpen, notesBookingId]);

  // The contact page, shown in a modal over these details by "View Contact"
  const [contactModalId, setContactModalId] = useState<string | null>(null);

  // The client's conversation id is looked up as soon as these details are showing, so that by
  // the time the chat button is tapped it is usually already known and the real conversation
  // opens at once (no blank shell). The lookup finds the conversation or creates it.
  const clientConversationIdRef = useRef<{ contactId: string; conversationId: string } | null>(null);
  const clientContactId = booking?.contact?.id;
  useEffect(() => {
    if (!isOpen || hidePayment || !clientContactId) return;
    if (clientConversationIdRef.current?.contactId === clientContactId) return;
    let cancelled = false;
    InboxService.startConversation(clientContactId)
      .then(conversation => {
        if (!cancelled && conversation?.id) {
          clientConversationIdRef.current = { contactId: clientContactId, conversationId: String(conversation.id) };
        }
      })
      .catch(() => { /* the tap falls back to looking it up then */ });
    return () => { cancelled = true; };
  }, [isOpen, hidePayment, clientContactId]);

  const handleMessageClient = () => {
    const contact = booking?.contact;
    if (!contact?.id) return;
    const contactId = contact.id;
    const contactName = contact.name || '';
    const avatarUrl = contactsCache.getAvatar(contactId) || contact.avatarUrl || undefined;

    // Already looked up: open the real conversation directly
    const known = clientConversationIdRef.current;
    if (known?.contactId === contactId) {
      setClientThread({ id: known.conversationId, contactName, avatarUrl });
      return;
    }

    // Not known yet: open a blank shell now and swap in the real id when it arrives
    const tempId = `temp-${Date.now()}`;
    try {
      sessionStorage.setItem(`conversationMeta:${tempId}`, JSON.stringify({ contactName, avatarUrl }));
    } catch {
      // ignore
    }
    setClientThread({ id: tempId, contactName, avatarUrl, isShell: true });
    InboxService.startConversation(contactId)
      .then(conversation => {
        if (!conversation?.id) return;
        try {
          sessionStorage.setItem(`conversationMeta:${String(conversation.id)}`, JSON.stringify({ contactName, avatarUrl }));
        } catch {
          // ignore
        }
        window.dispatchEvent(new CustomEvent('realConversationFound', {
          detail: { conversationId: conversation.id, contactName },
        }));
      })
      .catch(err => console.error('[BookingDetails] could not open the client conversation', err));
  };

  // Call the client: opens the dialer with their number and name already in it (the same
  // thing the conversation's call button does), over these details — nothing here closes.
  const handleCallClient = () => {
    const contact = booking?.contact;
    const phone = contact?.phone || '';
    if (import.meta.env.VITE_PLATFORM === 'desktop') {
      openIvrDialer(phone || undefined);
      return;
    }
    window.dispatchEvent(new CustomEvent('rs:openDialer', {
      detail: { phone, intent: 'keypad', contactName: contact?.name || undefined },
    }));
  };

  const openMaps = (address: string) => {
    const encodedAddress = encodeURIComponent(address);

    // Detect platform
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    if (isIOS) {
      // On iOS, use Apple Maps
      window.open(`maps://maps.apple.com/?q=${encodedAddress}`, '_system');
    } else if (isAndroid) {
      // On Android, use Google Maps app
      window.open(`geo:0,0?q=${encodedAddress}`, '_system');
    } else {
      // On desktop browsers, use Google Maps web
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapsUrl, '_blank');
    }
  };

  return (
    <>
      <SheetModal
        isOpen={isCancellationModalOpen}
        onClose={() => {
          setIsCancellationModalOpen(false);
          setFeeStep('selection');
          setFeeType(null);
          setCustomFeeAmount('');
        }}
        title={feeStep === 'selection' ? t('pages.bookingSystem.BookingDetails.cancelBooking') : t('pages.bookingSystem.BookingDetails.selectFeeType')}
        additionalContent={
          <div className={cancellationStyles.cancellationContainer}>
            {feeStep === 'selection' ? (
              <>
                {/* STEP 1: Fee Selection */}
                <div>
                  <div className={cancellationStyles.sectionTitle}>{t('pages.bookingSystem.BookingDetails.cancellationFee')}</div>
                  <div className={cancellationStyles.feeOptionsSection}>
                    <label className={`${cancellationStyles.feeOption} ${waiveFee ? cancellationStyles.selected : ''}`}>
                      <input
                        type="radio"
                        name="feeOption"
                        checked={waiveFee}
                        onChange={() => setWaiveFee(true)}
                        aria-label={t('pages.bookingSystem.BookingDetails.waiveCancellationFee')}
                      />
                      <div className={cancellationStyles.feeLabel}>
                        <span className={cancellationStyles.optionTitle}>{t('pages.bookingSystem.BookingDetails.waiveCancellationFee')}</span>
                        <span className={cancellationStyles.optionDescription}>{t('pages.bookingSystem.BookingDetails.noChargeToCustomer')}</span>
                      </div>
                    </label>

                    <label className={`${cancellationStyles.feeOption} ${!waiveFee ? cancellationStyles.selected : ''}`}>
                      <input
                        type="radio"
                        name="feeOption"
                        checked={!waiveFee}
                        onChange={() => setWaiveFee(false)}
                        aria-label={t('pages.bookingSystem.BookingDetails.applyCancellationFee')}
                      />
                      <div className={cancellationStyles.feeLabel}>
                        <span className={cancellationStyles.optionTitle}>{t('pages.bookingSystem.BookingDetails.applyCancellationFee')}</span>
                        <span className={cancellationStyles.optionDescription}>{t('pages.bookingSystem.BookingDetails.perServicePolicy')}</span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Notify Client */}
                <div className={cancellationStyles.notifySection}>
                  <div className={cancellationStyles.sectionTitle}>{t('pages.bookingSystem.BookingDetails.notification')}</div>
                  <div className={cancellationStyles.notifyCheckbox}>
                    <input
                      type="checkbox"
                      id="notifyClient"
                      checked={notifyClient}
                      onChange={(e) => setNotifyClient(e.target.checked)}
                      aria-label={t('pages.bookingSystem.BookingDetails.notifyClientViaEmail')}
                    />
                    <label htmlFor="notifyClient">{t('pages.bookingSystem.BookingDetails.notifyClientViaEmail')}</label>
                  </div>
                </div>

                {/* Message */}
                {notifyClient && (
                  <div className={cancellationStyles.messageSection}>
                    <label htmlFor="cancellationMsg">{t('pages.bookingSystem.BookingDetails.addMessageOptional')}</label>
                    <textarea
                      id="cancellationMsg"
                      value={cancellationMessage}
                      onChange={(e) => setCancellationMessage(e.target.value)}
                      placeholder={t('pages.bookingSystem.BookingDetails.enterAMessageToSendTo')}
                      aria-label={t('pages.bookingSystem.BookingDetails.cancellationMessage')}
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className={cancellationStyles.actionButtons}>
                  <button 
                    className={cancellationStyles.cancelBtn}
                    onClick={() => {
                      setIsCancellationModalOpen(false);
                      setFeeStep('selection');
                    }}
                  >{t('pages.bookingSystem.BookingDetails.cancel')}
                  </button>
                  <button 
                    className={waiveFee ? cancellationStyles.confirmBtn : cancellationStyles.nextBtn}
                    onClick={() => {
                      if (waiveFee) {
                        handleCancelbooking();
                      } else {
                        setFeeStep('type');
                        setFeeType(null);
                      }
                    }}
                  >
                    {waiveFee ? t('pages.bookingSystem.BookingDetails.confirmCancellation') : t('pages.bookingSystem.BookingDetails.next')}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* STEP 2: Fee Type Selection (only shown when applying fee) */}
                <div>
                  <div className={cancellationStyles.sectionTitle}>{t('pages.bookingSystem.BookingDetails.feeType')}</div>
                  <div className={cancellationStyles.feeTypeSection}>
                    <label className={`${cancellationStyles.feeTypeOption} ${feeType === 'standard' ? cancellationStyles.selected : ''}`}>
                      <input
                        type="radio"
                        name="feeType"
                        checked={feeType === 'standard'}
                        onChange={() => {
                          setFeeType('standard');
                          setStandardPreset(null);
                          setCustomFeeAmount('');
                        }}
                        aria-label={t('pages.bookingSystem.BookingDetails.standardCancellationFee')}
                      />
                      <div className={cancellationStyles.feeTypeLabel}>
                        <span className={cancellationStyles.feeTypeTitle}>{t('pages.bookingSystem.BookingDetails.standardFee')}</span>
                        <span className={cancellationStyles.feeTypeDescription}>{t('pages.bookingSystem.BookingDetails.useServiceSStandardCancellationFee')}</span>
                      </div>
                    </label>

                    <label className={`${cancellationStyles.feeTypeOption} ${feeType === 'custom' ? cancellationStyles.selected : ''}`}>
                      <input
                        type="radio"
                        name="feeType"
                        checked={feeType === 'custom'}
                        onChange={() => setFeeType('custom')}
                        aria-label={t('pages.bookingSystem.BookingDetails.customCancellationFee')}
                      />
                      <div className={cancellationStyles.feeTypeLabel}>
                        <span className={cancellationStyles.feeTypeTitle}>{t('pages.bookingSystem.BookingDetails.customFee')}</span>
                        <span className={cancellationStyles.feeTypeDescription}>{t('pages.bookingSystem.BookingDetails.enterACustomAmount')}</span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Standard Fee presets */}
                {feeType === 'standard' && (
                  <div className={cancellationStyles.feeTypeSection}>
                    {STANDARD_FEE_PRESETS.map(p => (
                      <label key={p.id} className={`${cancellationStyles.feeTypeOption} ${standardPreset === p.id ? cancellationStyles.selected : ''}`}>
                        <input
                          type="radio"
                          name="standardPreset"
                          checked={standardPreset === p.id}
                          onChange={() => setStandardPreset(p.id)}
                          aria-label={presetLabel(p.fee, jobPrice)}
                        />
                        <div className={cancellationStyles.feeTypeLabel}>
                          <span className={cancellationStyles.feeTypeTitle}>{presetLabel(p.fee, jobPrice)}</span>
                          <span className={cancellationStyles.feeTypeDescription}>{typeof p.fee === 'number' ? t('pages.bookingSystem.BookingDetails.flatFee') : t('pages.bookingSystem.BookingDetails.ofTheCleaningPrice')}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                {/* Custom Fee Input */}
                {feeType === 'custom' && (
                  <div className={cancellationStyles.customFeeInputSection}>
                    <label htmlFor="customFee">{t('pages.bookingSystem.BookingDetails.customFeeAmount')}</label>
                    <input
                      id="customFee"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={t('pages.bookingSystem.BookingDetails.enterAmount')}
                      value={customFeeAmount}
                      onChange={(e) => setCustomFeeAmount(e.target.value)}
                      aria-label={t('pages.bookingSystem.BookingDetails.customFeeAmount2')}
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className={cancellationStyles.actionButtons}>
                  <button 
                    className={`${cancellationStyles.cancelBtn} ${cancellationStyles.backButton}`}
                    onClick={() => {
                      setFeeStep('selection');
                      setFeeType(null);
                      setCustomFeeAmount('');
                    }}
                  >{t('pages.bookingSystem.BookingDetails.back')}
                  </button>
                  <button 
                    className={cancellationStyles.confirmBtn}
                    onClick={() => {
                      console.log('Confirm button clicked - feeType:', feeType, 'customFeeAmount:', customFeeAmount);
                      if (feeType) {
                        console.log('Fee type selected, calling handleCancelbooking...');
                        handleCancelbooking();
                      }
                    }}
                    disabled={!feeType || (feeType === 'custom' && !customFeeAmount) || (feeType === 'standard' && !standardPreset)}
                  >{t('pages.bookingSystem.BookingDetails.confirmCancellation')}
                  </button>
                </div>
              </>
            )}
          </div>
        }
        menuOptions={[]}
      />
      <SheetModal
        isOpen={isOptionsModalOpen}
        onClose={() => setIsOptionsModalOpen(false)}
        title={t('pages.bookingSystem.BookingDetails.manageBooking')}
        menuOptions={menuOptions}
      />
      {/* The contact's page, opened over these details by "View Contact" — back returns here */}
      <IonModal
        isOpen={!!contactModalId}
        onDidDismiss={() => setContactModalId(null)}
        className={styles.threadModal}
      >
        {contactModalId && (
          <Contact contactId={contactModalId} onClose={() => setContactModalId(null)} />
        )}
      </IonModal>
      {/* The client's conversation, opened over these details by the chat button */}
      <IonModal
        isOpen={!!clientThread}
        onDidDismiss={() => setClientThread(null)}
        className={styles.threadModal}
      >
        {clientThread ? (
          /* wrapInIonPage={false} means the thread paints no page background of its own — this wrapper
             supplies the app gradient, same as the softphone's thread does. */
          <div className={styles.threadModalWrapper}>
            <ConversationThread
              id={clientThread.id}
              onBack={() => setClientThread(null)}
              initialContactName={clientThread.contactName}
              initialAvatarUrl={clientThread.avatarUrl}
              initialIsBlankShell={clientThread.isShell}
              wrapInIonPage={false}
              onViewContact={(contactId: string) => {
                // go to the contact page once both modals have closed
                pendingDismissActionRef.current = () => ionRouter.push(`/tabs/contact/${contactId}`, 'forward', 'push');
                setClientThread(null);
                onClose();
              }}
            />
          </div>
        ) : null}
      </IonModal>
      <IonModal
        className={styles.detailsModal}
        isOpen={isOpen}
        onDidDismiss={runPendingDismissAction}
        canDismiss={true}
        backdropDismiss={true}
        keepContentsMounted={false}
        handle={false}
      >
        {/* Portal target for the Collect Payment PortalDropdown below — a
            direct IonModal child (fixed, inset:0) rather than nested inside
            IonButtons/IonFooter, which don't reliably render/size a plain
            non-IonButton child. */}
        <div ref={collectPaymentPortalRef} className={styles.pickerPortalRoot} />
        <IonHeader className="ion-no-border">
          <IonToolbar className={`${styles.customToolbar} glass-toolbar-no-dim`}>
            <IonButtons slot="start">
              <IonButton
                shape="round"
                className={`${styles.closeButton} glass-own-press-feedback`}
                onClick={onClose}
              >
                <IonIcon slot="icon-only" icon={close} />
              </IonButton>
            </IonButtons>
            {/* A direct child of the toolbar (not inside the start buttons) so Ionic centers it across the whole bar */}
            <IonTitle size="small">
              <CopyableIdTitle
                title={t('pages.bookingSystem.BookingDetails.bookingDetails')}
                id={booking?.bookingId || booking?.id}
              />
            </IonTitle>
            {!hidePayment && (
              <IonButtons slot="end" className={styles.endButtons}>
                <IonButton
                  className={styles.circleButtonEllipsis}
                  onClick={() => setIsOptionsModalOpen(true)}
                >
                  <IonIcon slot="icon-only" icon={ellipsisVertical} />
                </IonButton>
              </IonButtons>
            )}
          </IonToolbar>
        </IonHeader>
        <IonContent className={styles.content}>
          {error ? (
            <IonCard className={styles.stateCard}>
              <IonCardContent>
                <IonText color="danger">{error}</IonText>
              </IonCardContent>
            </IonCard>
          ) : booking || isInitialLoading ? (
            <IonCard className={styles.bookingCard}>
              {/* Hero */}
              <IonCardContent className={styles.hero}>
                <IonItem lines="none" className={styles.heroTop}>
                  <IonAvatar slot="start" className={styles.avatar}>
                    {isInitialLoading ? (
                      <IonSkeletonText animated style={{ width: '100%', height: '100%' }} />
                    ) : (
                      <img
                        src={proxyImage(
                          (booking?.contact?.id ? contactsCache.getAvatar(booking.contact.id) : undefined)
                          || booking?.contact?.avatarUrl
                        ) || getDefaultAvatar(booking?.contact?.name || 'Unknown')}
                        alt={booking?.contact?.name}
                        loading="eager"
                        onError={(e) => {
                          console.log(`Avatar failed to load for contact: ${booking?.contact?.id}`);
                          const imgElement = e.target as HTMLImageElement;
                          imgElement.src = getDefaultAvatar(booking?.contact?.name || 'Unknown');
                        }}
                      />
                    )}
                  </IonAvatar>
                  <IonLabel className={styles.heroInfo}>
                    <h2 className={styles.heroName}>
                      {isInitialLoading ? (
                        <IonSkeletonText animated style={{ width: '60%' }} />
                      ) : (
                        booking?.contact?.name
                      )}
                    </h2>
                    {!hidePayment ? (
                      <p className={styles.heroSub}>
                        <IonIcon icon={mailOutline} />
                        {isInitialLoading ? (
                          <IonSkeletonText animated style={{ width: '80%' }} />
                        ) : (
                          booking?.contact?.email
                        )}
                      </p>
                    ) : (booking?.bookingId || booking?.id) ? (
                      <p className={styles.heroSub}>
                        <IonIcon icon={documentOutline} />
                        {isInitialLoading ? (
                          <IonSkeletonText animated style={{ width: '60%' }} />
                        ) : (
                          <span className={styles.jobNum}>{booking?.bookingId || booking?.id}</span>
                        )}
                      </p>
                    ) : null}
                  </IonLabel>
                  {!isInitialLoading && (
                    <div slot="end" className={styles.heroPriceWrap}>
                      {!hidePayment && <IonNote className={styles.heroPrice}>{displayPriceText}</IonNote>}
                      {/* The paid / unpaid badge is harmless for staff to see; only amounts and totals are hidden from them */}
                      {(() => {
                        const paymentBadge = getPaymentStatusBadge(booking);
                        return paymentBadge ? (
                          <IonChip className={`${styles.chip} ${styles[`chip_${paymentBadge.color}`]} ${styles.heroPaymentChip}`}>
                            <IonLabel>{paymentBadge.label}</IonLabel>
                          </IonChip>
                        ) : null;
                      })()}
                    </div>
                  )}
                </IonItem>

                <IonLabel className={styles.heroChips}>
                  {!isInitialLoading && (() => {
                    const c = completionChip();
                    return (
                      <IonChip className={`${styles.chip} ${styles[`chip_${c.color}`]}`}>
                        <IonIcon icon={c.icon} />
                        <IonLabel>{c.label}</IonLabel>
                      </IonChip>
                    );
                  })()}
                  {!hidePayment && !isInitialLoading && (() => {
                    const orderBadge = getOrderBadge();
                    return orderBadge ? (
                      <IonChip className={`${styles.chip} ${styles[`chip_${orderBadge.color}`]}`}>
                        <IonLabel>{orderBadge.label}</IonLabel>
                      </IonChip>
                    ) : null;
                  })()}

                  {/* Message / call the client — moved from the header, next to the status chips */}
                  {!hidePayment && !isInitialLoading && (
                    <>
                      <IonButton
                        fill="clear"
                        className={styles.heroAction}
                        aria-label={t('pages.bookingSystem.BookingDetails.messageClient')}
                        onClick={handleMessageClient}
                      >
                        <IonIcon slot="icon-only" icon={chatbubbleOutline} />
                      </IonButton>
                      <IonButton
                        fill="clear"
                        className={styles.heroAction}
                        aria-label={t('pages.bookingSystem.BookingDetails.callClient')}
                        onClick={handleCallClient}
                      >
                        <IonIcon slot="icon-only" icon={callOutline} />
                      </IonButton>
                    </>
                  )}

                  {!isBookingFinished && !isInitialLoading && !isServiceOrderLoading && !isAttendanceLoading && (
                    <IonButton
                      className={`${styles.completeBtn} ${isCompleted || isAttended ? styles.completeBtnDone : ''}`}
                      onClick={handleStatusChange}
                      disabled={isUpdating || isInitialLoading || isBeforeAppointment}
                    >
                      {isUpdating ? (
                        <IonSpinner name="dots" />
                      ) : (
                        <>
                          <IonIcon icon={checkmarkCircle} />{t('pages.bookingSystem.BookingDetails.complete')}
                        </>
                      )}
                    </IonButton>
                  )}
                  {/* Only shown once the booking is finished — while it's
                      still open, "Invoiced" already shows via getOrderBadge()
                      next to the booking status instead, so this never sits
                      beside the (not-yet-pressed) Complete button. */}
                  {isOrderInvoiced && isBookingFinished && (
                    <IonChip className={`${styles.chip} ${styles.chip_primary} ${styles.invoicedBadge}`}>
                      <IonIcon icon={documentTextOutline} />
                      <IonLabel>{t('pages.bookingSystem.BookingDetails.invoiced')}</IonLabel>
                    </IonChip>
                  )}
                </IonLabel>
              </IonCardContent>

              {/* Service Details */}
              <IonCardContent className={styles.section}>
                <IonLabel className={styles.sectionTitle}>{t('pages.bookingSystem.BookingDetails.serviceDetails')}</IonLabel>
                <IonList lines="none" className={styles.fieldRows}>
                  <IonItem lines="none" className={styles.fieldRow}>
                    <IonIcon slot="start" icon={documentOutline} className={styles.fieldIcon} />
                    <IonLabel className={styles.fieldValue}>
                      {isInitialLoading ? (
                        <IonSkeletonText animated style={{ width: '50%' }} />
                      ) : (
                        booking?.serviceType
                      )}
                    </IonLabel>
                  </IonItem>
                  <IonItem lines="none" className={styles.fieldRow}>
                    <IonIcon slot="start" icon={calendarOutline} className={styles.fieldIcon} />
                    <IonLabel className={styles.fieldValue}>
                      {isInitialLoading ? (
                        <IonSkeletonText animated style={{ width: '70%' }} />
                      ) : (
                        booking?.date ? (() => {
                          // Fix timezone issues by explicitly handling the date parsing
                          const dateStr = booking!.date;
                          const dateParts = dateStr.split('-');
                          const bookingDate = dateParts.length === 3
                            ? new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]))
                            : new Date(dateStr);

                          return bookingDate.toLocaleDateString(intlLocale(), {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric'
                          });
                        })() : ''
                      )}
                    </IonLabel>
                  </IonItem>
                  <IonItem lines="none" className={styles.fieldRow}>
                    <IonIcon slot="start" icon={timeOutline} className={styles.fieldIcon} />
                    <IonLabel className={styles.fieldValue}>
                      {isInitialLoading ? (
                        <IonSkeletonText animated style={{ width: '60%' }} />
                      ) : (
                        (() => {
                          if (!booking) return '';
                          // First, ensure we're working with string values
                          const startTimeValue = booking.startTime instanceof Date
                            ? booking.startTime.toTimeString().substring(0, 5) // Get HH:MM
                            : booking.startTime;

                          const endTimeValue = booking.endTime instanceof Date
                            ? booking.endTime.toTimeString().substring(0, 5) // Get HH:MM
                            : booking.endTime;

                          try {
                            // Format the start time
                            const startDate = new Date(`2000/01/01 ${startTimeValue}`);
                            const formattedStart = startDate.toLocaleTimeString(intlLocale(), {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            });

                            // Format the end time
                            const endDate = new Date(`2000/01/01 ${endTimeValue}`);
                            const formattedEnd = endDate.toLocaleTimeString(intlLocale(), {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            });

                            return `${formattedStart} - ${formattedEnd}`;
                          } catch (error) {
                            console.error('Error formatting times:', error, {
                              startTime: booking?.startTime,
                              endTime: booking?.endTime,
                              startTimeValue,
                              endTimeValue
                            });

                            // Fallback to display raw values
                            return `${startTimeValue} - ${endTimeValue}`;
                          }
                        })()
                      )}
                    </IonLabel>
                  </IonItem>
                </IonList>
              </IonCardContent>

              {/* Staff & Location */}
              <IonCardContent className={styles.section}>
                <IonLabel className={styles.sectionTitle}>{t('pages.bookingSystem.BookingDetails.staffLocation')}</IonLabel>
                <IonList lines="none" className={styles.fieldRows}>
                  <IonItem lines="none" className={styles.fieldRow}>
                    <IonIcon slot="start" icon={personOutline} className={styles.fieldIcon} />
                    {isInitialLoading ? (
                      <IonLabel className={styles.fieldValue}><IonSkeletonText animated style={{ width: '60%' }} /></IonLabel>
                    ) : Array.isArray(booking?.staff) ? (
                      <IonLabel className={styles.fieldValueStack}>
                        {booking.staff.map((staffMember: string, index: number) => (
                          <p key={index} className={styles.fieldValue}>{staffMember}</p>
                        ))}
                      </IonLabel>
                    ) : (
                      <IonLabel className={styles.fieldValue}>{booking?.staff}</IonLabel>
                    )}
                  </IonItem>

                  <IonItem lines="none" className={styles.fieldRow}>
                    <IonIcon slot="start" icon={cubeOutline} className={styles.fieldIcon} />
                    <IonLabel className={styles.fieldValue}>
                      {isInitialLoading ? (
                        <IonSkeletonText animated style={{ width: '40%' }} />
                      ) : (
                        booking?.resource
                      )}
                    </IonLabel>
                  </IonItem>

                  <IonItem
                    button
                    lines="none"
                    detail={false}
                    className={styles.fieldRow}
                    onClick={() => !isInitialLoading && openMaps(`${booking?.contact?.address}`)}
                  >
                    <IonIcon slot="start" icon={locationSharp} className={styles.fieldIcon} />
                    <IonLabel className={styles.fieldValueEllipsis}>
                      {isInitialLoading ? (
                        <IonSkeletonText animated style={{ width: '70%' }} />
                      ) : (
                        booking?.contact?.address
                      )}
                    </IonLabel>
                  </IonItem>
                </IonList>
              </IonCardContent>

              {/* Notes — the booking's staff notes from Wix (only shown when there are some) */}
              {bookingNotes.trim() && (
                <IonCardContent className={styles.section}>
                  <IonLabel className={styles.sectionTitle}>{t('pages.bookingSystem.BookingDetails.notes')}</IonLabel>
                  <IonList lines="none" className={styles.fieldRows}>
                    <IonItem lines="none" className={styles.fieldRow}>
                      <IonIcon slot="start" icon={documentOutline} className={styles.fieldIcon} />
                      <IonLabel className={`${styles.fieldValue} ${styles.notesText}`}>{bookingNotes}</IonLabel>
                    </IonItem>
                  </IonList>
                </IonCardContent>
              )}

              {/* Media */}
              {booking && (
                <IonCardContent className={styles.section}>
                  <IonLabel className={styles.sectionTitle}>{t('pages.bookingSystem.BookingDetails.media')}</IonLabel>
                  <JobMediaManager
                    jobId={booking.id}
                    collectionId={JOB_MEDIA_COLLECTION_ID}
                    maxFiles={20}
                    compact
                  />
                </IonCardContent>
              )}
            </IonCard>
          ) : (
            <IonCard className={styles.stateCard}>
              <IonCardContent>
                <IonText>{t('pages.bookingSystem.BookingDetails.noBookingFound')}</IonText>
              </IonCardContent>
            </IonCard>
          )}
        </IonContent>
        {!hidePayment && (
          <IonFooter className={styles.footer}>
            <IonCard className={styles.footerWrapper}>
              <IonItem lines="none" className={styles.paymentStatus}>
                <IonLabel>
                  <p className={styles.amountLabel}>{t('pages.bookingSystem.BookingDetails.balanceDue')}</p>
                  <h2 className={styles.amount}>{isPaidInFull ? '$0.00' : displayPriceText}</h2>
                </IonLabel>
                {!isInitialLoading && !isPaidInFull && (() => {
                  const paymentBadge = getPaymentStatusBadge(booking);
                  return paymentBadge ? (
                    <IonChip slot="end" className={`${styles.chip} ${styles[`chip_${paymentBadge.color}`]}`}>
                      <IonLabel>{paymentBadge.label}</IonLabel>
                    </IonChip>
                  ) : null;
                })()}
                <IonButton
                  ref={collectPaymentTriggerRef}
                  slot="end"
                  className={styles.paymentButton}
                  disabled={isPaidInFull}
                  onClick={() => setShowCollectPaymentMenu(v => !v)}
                >
                  {isPaidInFull ? t('pages.bookingSystem.BookingDetails.paid') : t('pages.bookingSystem.BookingDetails.collectPayment')}
                </IonButton>
              </IonItem>
            </IonCard>
          </IonFooter>
        )}

        <PortalDropdown
          isOpen={showCollectPaymentMenu}
          anchorRef={collectPaymentTriggerRef}
          portalRef={collectPaymentPortalRef}
          onClose={() => setShowCollectPaymentMenu(false)}
          align="end"
          title={t('pages.bookingSystem.BookingDetails.collectPayment')}
          compact
          options={[
            {
              value: 'direct-pay',
              label: t('pages.bookingSystem.BookingDetails.directPay'),
              sublabel: 'Charge a card via Helcim',
              icon: cardOutline,
              color: 'primary',
              handler: () => setIsHelcimCheckoutOpen(true),
            },
            {
              value: 'create-invoice',
              label: t('pages.bookingSystem.BookingDetails.createInvoiceFromServiceOrder'),
              sublabel: salesOrder
                ? `From ${salesOrder.salesorder_number}`
                : t('pages.bookingSystem.BookingDetails.noLinkedServiceOrder'),
              icon: documentAttachOutline,
              color: salesOrder ? 'primary' : 'medium',
              handler: salesOrder ? () => setIsCreateInvoiceOpen(true) : undefined,
            },
          ]}
        />

        <HelcimCheckoutModal
          isOpen={isHelcimCheckoutOpen}
          onClose={() => setIsHelcimCheckoutOpen(false)}
          amount={booking?.price ?? 0}
          invoiceNumber={salesOrder?.salesorder_number}
          onSuccess={() => {
            DataStorageService.storeData('booking-refresh-trigger', Date.now());
          }}
        />

        <IonModal isOpen={isCreateInvoiceOpen} onDidDismiss={() => setIsCreateInvoiceOpen(false)} handle={false}>
          {isCreateInvoiceOpen && salesOrder && (
            <CreateInvoice
              onClose={() => setIsCreateInvoiceOpen(false)}
              prefill={{
                // Without this the form defaults to Service Order type (see
                // CreateInvoice.tsx's own invoiceType default) — the sales
                // order link below only takes effect on the invoice payload
                // path, so it needs this to actually run.
                invoiceType: 'INVOICE',
                customerName: salesOrder.customer_name,
                contactId: salesOrder.customer_id,
                linkedSalesOrderNumber: salesOrder.salesorder_number,
                lineItems: (salesOrder.line_items ?? []).map((li) => ({
                  // Exact item name, nothing appended — see
                  // CreateInvoicePrefillLineItem's doc comment for why.
                  description: li.name,
                  itemDescription: li.description,
                  quantity: li.quantity,
                  price: li.rate,
                  discountAmount: li.discount ? String(li.discount) : '',
                  taxRate: li.tax_percentage ? String(li.tax_percentage / 100) : '',
                })),
              }}
            />
          )}
        </IonModal>

        <IonModal isOpen={isInvoiceOpen} onDidDismiss={closeInvoiceModal} handle={false}>
          {isInvoiceOpen && (
            <Invoice
              documentType="invoice"
              invoiceId={invoiceModalId ?? undefined}
              resolvingId={invoiceModalId === null}
              onClose={closeInvoiceModal}
            />
          )}
        </IonModal>

        <IonModal isOpen={isServiceOrderOpen} onDidDismiss={() => setIsServiceOrderOpen(false)} handle={false}>
          {isServiceOrderOpen && salesOrder && (
            <ServiceOrderDetail salesorderId={salesOrder.salesorder_id} onClose={() => setIsServiceOrderOpen(false)} />
          )}
        </IonModal>
      </IonModal>
    </>
  );
};

export default BookingDetailsModal;